import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../context/AppContext';
import { 
  Save, 
  Server, 
  Code, 
  Users, 
  Globe, 
  HardDrive, 
  CheckCircle, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle,
  Search,
  X,
  Plus,
  UserCheck,
  Shield,
  Palette,
  Upload,
  Filter,
  ArrowRight,
  Layers,
  Image as ImageIcon,
  FileText,
  MessageSquare,
  Send,
  Smartphone,
  Eye,
  EyeOff,
  Sparkles,
  Radio,
  Check,
  ExternalLink,
  BellRing
} from 'lucide-react';
import { DEFAULT_CARD_HTML_TEMPLATE } from '../../utils/signatureGenerator';

const MailSystemSettings = () => {
  const { 
    commonSignature, 
    setCommonSignature, 
    groupMails, 
    sesLogs, 
    getUserSignature, 
    users, 
    updateStorageQuota, 
    dnsSettings, 
    updateDnsSettings,
    updateGroupMailAssignees,
    getUserStorageUsage,
    logoSize,
    setLogoSize,
    customFonts,
    setCustomFonts,
    customLogoUrl,
    setCustomLogoUrl,
    loginLogoSize,
    setLoginLogoSize,
    signatureFormat,
    setSignatureFormat,
    kakaoBizConfig,
    updateKakaoBizConfig,
    kakaoDispatchLogs,
    sendKakaoBizMessage,
    DEFAULT_KAKAO_TEMPLATES
  } = useContext(AppContext);

  const [adminSigFormat, setAdminSigFormat] = useState(signatureFormat || 'CARD_IMAGE');
  const [signatureHtml, setSignatureHtml] = useState(commonSignature || DEFAULT_CARD_HTML_TEMPLATE);
  const [activeSubTab, setActiveSubTab] = useState('signature'); // 'signature' | 'groups' | 'dns' | 'quota' | 'logs' | 'design' | 'kakao'

  // Kakao Biz Configuration States
  const [kEnabled, setKEnabled] = useState(kakaoBizConfig?.enabled ?? true);
  const [kProvider, setKProvider] = useState(kakaoBizConfig?.provider || 'solapi');
  const [kChannelId, setKChannelId] = useState(kakaoBizConfig?.channelId || '@clouflake');
  const [kSenderKey, setKSenderKey] = useState(kakaoBizConfig?.senderKey || 'PFID-CLOUFLAKE-BIZ-7731');
  const [kApiKey, setKApiKey] = useState(kakaoBizConfig?.apiKey || '');
  const [kApiSecret, setKApiSecret] = useState(kakaoBizConfig?.apiSecret || '');
  const [kSenderPhone, setKSenderPhone] = useState(kakaoBizConfig?.senderPhone || '02-123-4567');
  const [kAutoNotice, setKAutoNotice] = useState(kakaoBizConfig?.autoNoticeEnabled ?? true);
  const [kAutoMailSummary, setKAutoMailSummary] = useState(kakaoBizConfig?.autoMailSummaryEnabled ?? true);
  const [kFailoverSms, setKFailoverSms] = useState(kakaoBizConfig?.failoverSms ?? true);
  const [showSecret, setShowSecret] = useState(false);
  const [kakaoSavedToast, setKakaoSavedToast] = useState(false);

  // Kakao Template Customization States
  const [selectedTplKey, setSelectedTplKey] = useState('LEAVE_REQUEST');
  const [templates, setTemplates] = useState(() => kakaoBizConfig?.templates || DEFAULT_KAKAO_TEMPLATES || {});

  // Kakao Test Send States
  const [testPhone, setTestPhone] = useState('010-2101-0009');
  const [testType, setTestType] = useState('LEAVE_REQUEST');
  const [testTitle, setTestTitle] = useState('[클라우플레이크 전자결재 알림]');
  const [testContent, setTestContent] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Sync state when kakaoBizConfig changes
  useEffect(() => {
    if (kakaoBizConfig) {
      setKEnabled(kakaoBizConfig.enabled ?? true);
      setKProvider(kakaoBizConfig.provider || 'solapi');
      setKChannelId(kakaoBizConfig.channelId || '@clouflake');
      setKSenderKey(kakaoBizConfig.senderKey || 'PFID-CLOUFLAKE-BIZ-7731');
      setKApiKey(kakaoBizConfig.apiKey || '');
      setKApiSecret(kakaoBizConfig.apiSecret || '');
      setKSenderPhone(kakaoBizConfig.senderPhone || '02-123-4567');
      setKAutoNotice(kakaoBizConfig.autoNoticeEnabled ?? true);
      setKAutoMailSummary(kakaoBizConfig.autoMailSummaryEnabled ?? true);
      setKFailoverSms(kakaoBizConfig.failoverSms ?? true);
      if (kakaoBizConfig.templates) {
        setTemplates(prev => ({ ...(DEFAULT_KAKAO_TEMPLATES || {}), ...kakaoBizConfig.templates }));
      }
    }
  }, [kakaoBizConfig]);

  // Current active template
  const activeTpl = templates[selectedTplKey] || (DEFAULT_KAKAO_TEMPLATES && DEFAULT_KAKAO_TEMPLATES[selectedTplKey]) || {};

  // Sample data substitution for real-time smartphone preview
  const getRenderedPreview = (tplKey) => {
    const tpl = templates[tplKey] || (DEFAULT_KAKAO_TEMPLATES && DEFAULT_KAKAO_TEMPLATES[tplKey]) || {};
    let text = tpl.templateText || '';
    const sampleVals = {
      신청자명: '루시',
      부서: '클라우드네이티브',
      직급: '사원',
      연차구분: '연차',
      일수: '1',
      시작일: '2026-08-20',
      종료일: '2026-08-20',
      사유: '개인 사유',
      청구금액: '45,000',
      사용내역: '[식대] 팀 점심 식사',
      카드사: '법인 국민카드',
      카드번호: '4521',
      공지제목: '2026년 3분기 전사 타운홀 미팅 안내',
      작성자명: '문융희 팀장',
      등록일시: '2026-08-20 09:00',
      공지내용: '금주 금요일 16시 대회의실에서 3분기 성과 공유 및 신규 로드맵 발표가 진행됩니다.',
      발신자: 'partner@megacloud.com',
      메일제목: 'Q3 클라우드 인프라 설계 제안서 검토 요청',
      수신일시: '2026-08-20 10:30',
      요약내용: '1. 제안서 초안 전달 및 보안 아키텍처 항목 검토 요청\n2. 8/21(목) 18:00까지 피드백 필요\n3. 금요일 오전 미팅 일정 조율 희망',
      신청항목: '연차 (2026-08-20)',
      결재결과: '✅ 최종 승인 완료',
      결재자명: '문융희',
      결재자직급: '팀장',
      반려사유: ''
    };

    Object.entries(sampleVals).forEach(([k, v]) => {
      text = text.replace(new RegExp(`#{${k}}`, 'g'), v);
    });
    return text;
  };

  const handleUpdateActiveTemplate = (field, value) => {
    setTemplates(prev => ({
      ...prev,
      [selectedTplKey]: {
        ...(prev[selectedTplKey] || DEFAULT_KAKAO_TEMPLATES[selectedTplKey] || {}),
        [field]: value
      }
    }));
  };

  const handleInsertVariable = (tag) => {
    const currentText = activeTpl.templateText || '';
    handleUpdateActiveTemplate('templateText', currentText + ' ' + tag);
  };

  const handleResetTemplate = (tplKey) => {
    if (!DEFAULT_KAKAO_TEMPLATES || !DEFAULT_KAKAO_TEMPLATES[tplKey]) return;
    setTemplates(prev => ({
      ...prev,
      [tplKey]: { ...DEFAULT_KAKAO_TEMPLATES[tplKey] }
    }));
  };

  const handleSaveKakaoConfig = async (e) => {
    if (e) e.preventDefault();
    const newConfig = {
      ...(kakaoBizConfig || {}),
      enabled: kEnabled,
      provider: kProvider,
      channelId: kChannelId,
      senderKey: kSenderKey,
      apiKey: kApiKey,
      apiSecret: kApiSecret,
      senderPhone: kSenderPhone,
      autoNoticeEnabled: kAutoNotice,
      autoMailSummaryEnabled: kAutoMailSummary,
      failoverSms: kFailoverSms,
      templates: templates,
      updatedAt: new Date().toISOString()
    };

    if (updateKakaoBizConfig) {
      await updateKakaoBizConfig(newConfig);
    }
    setKakaoSavedToast(true);
    setTimeout(() => setKakaoSavedToast(false), 3500);
  };

  const handleSendKakaoTest = async () => {
    if (!testPhone.trim()) {
      alert('테스트 수신자 휴대폰 번호를 입력해 주세요.');
      return;
    }

    setIsSendingTest(true);
    setTestResult(null);

    // Auto-save the config first
    await handleSaveKakaoConfig();

    const renderedMsg = getRenderedPreview(selectedTplKey);
    const currTpl = templates[selectedTplKey] || (DEFAULT_KAKAO_TEMPLATES && DEFAULT_KAKAO_TEMPLATES[selectedTplKey]) || {};

    try {
      const res = await sendKakaoBizMessage({
        recipientPhone: testPhone,
        recipientName: '관리자 테스트',
        title: currTpl.title || '[클라우플레이크 알림]',
        messageText: renderedMsg,
        buttonText: currTpl.buttonText || '확인하기',
        buttonUrl: currTpl.buttonUrl ? `${window.location.origin}${currTpl.buttonUrl}` : window.location.origin,
        messageType: selectedTplKey,
        apiKey: kApiKey,
        apiSecret: kApiSecret,
        senderKey: kSenderKey,
        senderPhone: kSenderPhone,
        provider: kProvider
      });

      setTestResult({
        success: true,
        message: `[카카오톡 발송 성공] ${testPhone} 번호로 '${currTpl.name || selectedTplKey}' 서식 메시지가 성공적으로 발송되었습니다.`,
        data: res
      });
    } catch (err) {
      setTestResult({
        success: false,
        message: `[발송 거부/오류] ${err.message || '카카오 비즈니스 게이트웨이 통신 실패'}`
      });
    } finally {
      setIsSendingTest(false);
    }
  };

  // Design customizing states
  const [logoPcW, setLogoPcW] = useState(logoSize?.pcWidth || 160);
  const [logoPcH, setLogoPcH] = useState(logoSize?.pcHeight || 48);
  const [logoMobileW, setLogoMobileW] = useState(logoSize?.mobileWidth || 120);
  const [logoMobileH, setLogoMobileH] = useState(logoSize?.mobileHeight || 36);

  const [loginLogoW, setLoginLogoW] = useState(loginLogoSize?.width || 120);
  const [loginLogoH, setLoginLogoH] = useState(loginLogoSize?.height || 120);
  const [loginLogoPad, setLoginLogoPad] = useState(loginLogoSize?.padding ?? 12);

  const [fontMenuFamily, setFontMenuFamily] = useState(customFonts?.menuFamily || 'Pretendard');
  const [fontMenuSize, setFontMenuSize] = useState(customFonts?.menuSize || 14);
  const [fontMenuWeight, setFontMenuWeight] = useState(customFonts?.menuWeight || 'bold');

  const [fontContentFamily, setFontContentFamily] = useState(customFonts?.contentFamily || 'Pretendard');
  const [fontContentSize, setFontContentSize] = useState(customFonts?.contentSize || 13);
  const [fontContentWeight, setFontContentWeight] = useState(customFonts?.contentWeight || 'normal');

  // Logo upload and cropping states
  const [logoRawImage, setLogoRawImage] = useState(null);
  const [logoZoom, setLogoZoom] = useState(1);
  const [logoOffsetX, setLogoOffsetX] = useState(0);
  const [logoOffsetY, setLogoOffsetY] = useState(0);

  // Real-time rectangular logo cropper using canvas (320x96)
  React.useEffect(() => {
    if (!logoRawImage) return;

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 320;
      canvas.height = 96;
      const ctx = canvas.getContext('2d');

      // White background fill
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 320, 96);

      // Aspect ratio scale: Visual crop area is 200x60, Canvas is 320x96 (scale factor is 320/200 = 1.6)
      const scale = 320 / 200;
      const drawZoom = logoZoom * scale;
      const drawX = logoOffsetX * scale;
      const drawY = logoOffsetY * scale;

      const imgRatio = img.width / img.height;
      let w = 320;
      let h = 96;
      if (imgRatio > (320 / 96)) {
        h = 96;
        w = 96 * imgRatio;
      } else {
        w = 320;
        h = 320 / imgRatio;
      }

      w *= drawZoom;
      h *= drawZoom;

      ctx.drawImage(img, 160 + drawX - w / 2, 48 + drawY - h / 2, w, h);

      const croppedUrl = canvas.toDataURL('image/png');
      setCustomLogoUrl(croppedUrl);
    };
    img.src = logoRawImage;
  }, [logoRawImage, logoZoom, logoOffsetX, logoOffsetY]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogoRawImage(event.target.result);
        setLogoZoom(1);
        setLogoOffsetX(0);
        setLogoOffsetY(0);
      };
      reader.readAsDataURL(file);
    }
  };

  // DNS States
  const [domain, setDomain] = useState(dnsSettings?.Domain || 'clouflake.com');
  const [mxRecord, setMxRecord] = useState(dnsSettings?.MxRecord || '10 feedback-smtp.ap-northeast-2.amazonses.com');
  const [spfRecord, setSpfRecord] = useState(dnsSettings?.SpfRecord || 'v=spf1 include:amazonses.com ~all');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifySuccess, setVerifySuccess] = useState(dnsSettings?.IsVerified ?? true);

  // Quota States
  const [selectedUserId, setSelectedUserId] = useState(users[0]?.UserID || '');
  const [newQuota, setNewQuota] = useState('10');

  // Group Mail Editor Modal States (Requirement 1)
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [groupSearchTerm, setGroupSearchTerm] = useState('');

  // Logs Search and Filter States (Requirement 2)
  const [logFilterType, setLogFilterType] = useState('ALL'); // 'ALL' | 'SEND' | 'RECEIVE' | 'FAILED'
  const [logSearchTerm, setLogSearchTerm] = useState('');

  const handleSaveSignature = (e) => {
    e.preventDefault();
    setSignatureFormat(adminSigFormat);
    setCommonSignature(signatureHtml);
    alert(`회사 공통 메일 서명(${adminSigFormat === 'CARD_IMAGE' ? '명함 이미지 PNG' : adminSigFormat === 'CARD_HTML' ? '명함 HTML 카드' : '사용자 정의 HTML'})이 저장되었습니다. 모든 신규 발송 메일에 자동 적용됩니다.`);
  };

  const handleSaveDesignSettings = (e) => {
    e.preventDefault();
    setLogoSize({
      pcWidth: Number(logoPcW),
      pcHeight: Number(logoPcH),
      mobileWidth: Number(logoMobileW),
      mobileHeight: Number(logoMobileH)
    });
    setLoginLogoSize({
      width: Number(loginLogoW),
      height: Number(loginLogoH),
      padding: Number(loginLogoPad)
    });
    setCustomFonts({
      menuFamily: fontMenuFamily,
      menuSize: Number(fontMenuSize),
      menuWeight: fontMenuWeight,
      contentFamily: fontContentFamily,
      contentSize: Number(fontContentSize),
      contentWeight: fontContentWeight
    });
    alert('포털 로고 크기 및 메뉴/내용별 폰트 디자인 설정이 실시간 데이터베이스에 성공적으로 영구 동기화되었습니다!');
  };

  const handleVerifyDns = () => {
    setIsVerifying(true);
    setTimeout(() => {
      setIsVerifying(false);
      setVerifySuccess(true);
      updateDnsSettings({
        Domain: domain,
        MxRecord: mxRecord,
        SpfRecord: spfRecord,
        IsVerified: true
      });
      alert('메일 서버 DNS 세팅이 완료되었습니다. MX 레코드 및 SPF 정책 검증(Verified)이 정상 종료되었습니다.');
    }, 1200);
  };

  const handleQuotaSubmit = (e) => {
    e.preventDefault();
    if (!selectedUserId || !newQuota) {
      alert('사용자 및 할당 용량을 설정해 주세요.');
      return;
    }
    updateStorageQuota(selectedUserId, newQuota);
    alert('해당 계정의 메일 저장소 할당 용량이 정상적으로 업데이트되었습니다.');
  };

  return (
    <div className="space-y-6 text-left">
      {/* Sub tabs nav */}
      <div className="flex flex-wrap border-b border-slate-200 pb-px gap-1">
        <button
          onClick={() => setActiveSubTab('signature')}
          className={`flex items-center gap-2 px-4 py-3 border-b-2 font-bold text-xs sm:text-sm transition-all cursor-pointer ${
            activeSubTab === 'signature'
              ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50 rounded-t-xl'
              : 'border-transparent text-slate-500 hover:text-slate-900'
          }`}
        >
          <Code className="w-4 h-4" />
          공통 서명 HTML
        </button>

        <button
          onClick={() => setActiveSubTab('dns')}
          className={`flex items-center gap-2 px-4 py-3 border-b-2 font-bold text-xs sm:text-sm transition-all cursor-pointer ${
            activeSubTab === 'dns'
              ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50 rounded-t-xl'
              : 'border-transparent text-slate-500 hover:text-slate-900'
          }`}
        >
          <Globe className="w-4 h-4" />
          메일 서버 DNS (MX/SPF)
        </button>

        <button
          onClick={() => setActiveSubTab('quota')}
          className={`flex items-center gap-2 px-4 py-3 border-b-2 font-bold text-xs sm:text-sm transition-all cursor-pointer ${
            activeSubTab === 'quota'
              ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50 rounded-t-xl'
              : 'border-transparent text-slate-500 hover:text-slate-900'
          }`}
        >
          <HardDrive className="w-4 h-4" />
          메일 용량 할당 관리
        </button>

        <button
          onClick={() => setActiveSubTab('groups')}
          className={`flex items-center gap-2 px-4 py-3 border-b-2 font-bold text-xs sm:text-sm transition-all cursor-pointer ${
            activeSubTab === 'groups'
              ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50 rounded-t-xl'
              : 'border-transparent text-slate-500 hover:text-slate-900'
          }`}
        >
          <Users className="w-4 h-4" />
          대표 그룹 메일
        </button>

        <button
          onClick={() => setActiveSubTab('logs')}
          className={`flex items-center gap-2 px-4 py-3 border-b-2 font-bold text-xs sm:text-sm transition-all cursor-pointer ${
            activeSubTab === 'logs'
              ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50 rounded-t-xl'
              : 'border-transparent text-slate-500 hover:text-slate-900'
          }`}
        >
          <Server className="w-4 h-4" />
          SES 수발신 감사 로그
        </button>

        <button
          onClick={() => setActiveSubTab('design')}
          className={`flex items-center gap-2 px-4 py-3 border-b-2 font-bold text-xs sm:text-sm transition-all cursor-pointer ${
            activeSubTab === 'design'
              ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50 rounded-t-xl'
              : 'border-transparent text-slate-500 hover:text-slate-900'
          }`}
        >
          <Palette className="w-4 h-4" />
          포털 디자인 커스텀
        </button>

        <button
          onClick={() => setActiveSubTab('kakao')}
          className={`flex items-center gap-2 px-4 py-3 border-b-2 font-bold text-xs sm:text-sm transition-all cursor-pointer ${
            activeSubTab === 'kakao'
              ? 'border-amber-500 text-amber-900 bg-amber-50/60 rounded-t-xl'
              : 'border-transparent text-slate-500 hover:text-slate-900'
          }`}
        >
          <MessageSquare className="w-4 h-4 text-amber-500" />
          <span className="flex items-center gap-1.5">
            카카오 비즈니스 연동
            <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold bg-amber-400 text-slate-900">HOT</span>
          </span>
        </button>
      </div>

      {/* Sub Tab Contents */}
      
      {/* 1. Signature Tab */}
      {activeSubTab === 'signature' && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-6">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-sm font-bold text-slate-900">전사 공통 메일 서명(Signature) 편집기</h3>
              <p className="text-xs text-slate-500 mt-1">임직원 전체가 발송하는 모든 이메일 하단에 첨부될 표준 명함 서식 및 형태를 설정합니다.</p>
            </div>
            <span className="text-[10px] text-indigo-700 font-bold bg-indigo-50 border border-indigo-200 px-2 py-1 rounded-lg">SES API 연동 활성</span>
          </div>

          <form onSubmit={handleSaveSignature} className="space-y-5">
            {/* Format Selector */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-700">전사 기본 서명 형태 선택</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setAdminSigFormat('CARD_HTML');
                    setSignatureHtml(DEFAULT_CARD_HTML_TEMPLATE);
                  }}
                  className={`p-4 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between gap-2 ${
                    adminSigFormat === 'CARD_HTML'
                      ? 'border-indigo-600 bg-indigo-50/60 ring-2 ring-indigo-500/20'
                      : 'border-slate-200 bg-slate-50 hover:bg-slate-100/80'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <Layers className="w-4 h-4 text-indigo-600" />
                      명함형 HTML 카드 (권장)
                    </span>
                    {adminSigFormat === 'CARD_HTML' && <span className="w-2 h-2 rounded-full bg-indigo-600"></span>}
                  </div>
                  <span className="text-[11px] text-slate-500 leading-relaxed">
                    테이블 기반 고정 너비 레이아웃으로 공백 왜곡을 100% 방지하며 전화/메일/웹사이트 클릭 링크 유지
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setAdminSigFormat('CARD_IMAGE')}
                  className={`p-4 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between gap-2 ${
                    adminSigFormat === 'CARD_IMAGE'
                      ? 'border-indigo-600 bg-indigo-50/60 ring-2 ring-indigo-500/20'
                      : 'border-slate-200 bg-slate-50 hover:bg-slate-100/80'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <ImageIcon className="w-4 h-4 text-indigo-600" />
                      명함형 이미지 PNG
                    </span>
                    {adminSigFormat === 'CARD_IMAGE' && <span className="w-2 h-2 rounded-full bg-indigo-600"></span>}
                  </div>
                  <span className="text-[11px] text-slate-500 leading-relaxed">
                    2x Retina 고해상도 Canvas 이미지로 아웃룩, 지메일, 스마트폰 등 어떤 메일 뷰어에서도 완벽 고정
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setAdminSigFormat('CUSTOM_HTML')}
                  className={`p-4 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between gap-2 ${
                    adminSigFormat === 'CUSTOM_HTML'
                      ? 'border-indigo-600 bg-indigo-50/60 ring-2 ring-indigo-500/20'
                      : 'border-slate-200 bg-slate-50 hover:bg-slate-100/80'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <FileText className="w-4 h-4 text-indigo-600" />
                      사용자 정의 HTML
                    </span>
                    {adminSigFormat === 'CUSTOM_HTML' && <span className="w-2 h-2 rounded-full bg-indigo-600"></span>}
                  </div>
                  <span className="text-[11px] text-slate-500 leading-relaxed">
                    직접 HTML 소스코드를 입력하여 독자적인 서식 템플릿 구성
                  </span>
                </button>
              </div>
            </div>

            {adminSigFormat === 'CUSTOM_HTML' && (
              <div className="space-y-1.5 animate-in fade-in duration-200">
                <label className="text-xs font-bold text-slate-700">HTML 소스코드 편집</label>
                <textarea
                  rows="8"
                  value={signatureHtml}
                  onChange={e => setSignatureHtml(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl font-mono text-xs text-slate-900 focus:outline-none focus:border-indigo-600 leading-normal"
                />
              </div>
            )}

            {/* Signature Preview */}
            <div className="border border-slate-200 p-5 rounded-xl bg-slate-50 space-y-3">
              <span className="text-xs font-bold text-indigo-700 uppercase tracking-wide">
                실시간 서명 미리보기 ({adminSigFormat === 'CARD_IMAGE' ? '2x Retina PNG 이미지' : '표준 명함 HTML 카드'}):
              </span>
              <div className="bg-white p-4 rounded-xl shadow-2xs border border-slate-200 overflow-x-auto min-h-[80px]" dangerouslySetInnerHTML={{ __html: getUserSignature({
                Name: '이상희',
                JobTitle: '이사',
                Department: 'AI서비스개발',
                Phone: '010-2101-0009',
                Email: 'anderson@clouflake.com'
              }, adminSigFormat === 'CUSTOM_HTML' ? signatureHtml : null, adminSigFormat) }} />
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-6 rounded-xl shadow-sm transition-colors cursor-pointer text-xs"
              >
                <Save className="w-4 h-4" />
                서명 설정 저장
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 2. DNS Settings Tab */}
      {activeSubTab === 'dns' && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-6 text-left">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-sm font-bold text-slate-900">메일 서버 도메인 및 DNS (MX / SPF) 설정</h3>
              <p className="text-xs text-slate-500 mt-1">도메인 라우팅을 위한 MX 레코드와 스팸 필터 인증을 위한 SPF 정책을 설정하고 즉시 검증합니다.</p>
            </div>
          </div>

          {/* Verification Status Banner */}
          {isVerifying ? (
            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 text-center flex items-center justify-center gap-2.5 animate-pulse">
              <RefreshCw className="w-5 h-5 text-indigo-600 animate-spin" />
              <span className="text-xs text-slate-700 font-semibold">SES 메일 게이트웨이 및 DNS 레코드 상태 스캔 중...</span>
            </div>
          ) : verifySuccess ? (
            <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-2xl flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <span className="text-xs font-bold text-emerald-800 block">정상 연결됨 (Verified)</span>
                <p className="text-xs text-slate-600 leading-relaxed">
                  도메인 <strong>{domain}</strong>의 MX 레코드와 SPF 레코드가 AWS SES 인프라와 정상적으로 연결되었습니다.<br />
                  <span className="text-[10px] text-slate-400">동기화 일시: {new Date(dnsSettings?.LastVerifiedAt || Date.now()).toLocaleString()}</span>
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-2xl flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <span className="text-xs font-bold text-amber-800 block">검증 대기 중 (DNS Unverified)</span>
                <p className="text-xs text-slate-600 leading-relaxed">
                  도메인 설정을 변경했거나 신규 연결 시 하단의 레코드 검증이 필요합니다.
                </p>
              </div>
            </div>
          )}

          {/* Configuration Form */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 p-5 rounded-2xl border border-slate-200">
            <div className="space-y-1.5 text-xs">
              <label className="text-[10px] font-bold text-slate-600 uppercase block">연동 도메인 (Domain)</label>
              <input
                type="text"
                value={domain}
                onChange={e => setDomain(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
                placeholder="domain.com"
              />
            </div>

            <div className="space-y-1.5 text-xs">
              <label className="text-[10px] font-bold text-slate-600 uppercase block">MX Record (수신 라우팅)</label>
              <input
                type="text"
                value={mxRecord}
                onChange={e => setMxRecord(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600 font-mono"
              />
            </div>

            <div className="space-y-1.5 text-xs">
              <label className="text-[10px] font-bold text-slate-600 uppercase block">SPF Record (스팸 방지)</label>
              <input
                type="text"
                value={spfRecord}
                onChange={e => setSpfRecord(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600 font-mono"
              />
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={handleVerifyDns}
              disabled={isVerifying}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-6 rounded-xl shadow-sm transition-colors cursor-pointer text-xs"
            >
              <RefreshCw className={`w-4 h-4 ${isVerifying ? 'animate-spin' : ''}`} />
              DNS 레코드 변경 적용 및 서버 실시간 검증
            </button>
          </div>
        </div>
      )}

      {/* 3. Storage Quota Tab */}
      {activeSubTab === 'quota' && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-6 text-left">
          <div>
            <h3 className="text-sm font-bold text-slate-900">계정별 메일 저장 공간 할당 및 회수</h3>
            <p className="text-xs text-slate-500 mt-1">임직원별 이메일 스토리지 사용량을 확인하고 계정별 최대 허용 용량을 설정합니다.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Storage Quota Table */}
            <div className="lg:col-span-2 border border-slate-200 rounded-xl bg-slate-50 overflow-hidden">
              <div className="max-h-[300px] overflow-y-auto">
                <table className="w-full text-left text-xs text-slate-700">
                  <thead className="bg-slate-100 text-[10px] font-bold text-slate-500 uppercase tracking-wider sticky top-0">
                    <tr>
                      <th className="px-4 py-3">임직원</th>
                      <th className="px-4 py-3">부서 / 직급</th>
                      <th className="px-4 py-3">저장소 사용 현황 (사용량 / 할당량)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                     {users.map(u => {
                      const storageInfo = getUserStorageUsage ? getUserStorageUsage(u.UserID) : { usedMB: 0.1, quotaGB: u.StorageQuota || 10, usagePercent: 0.1 };
                      const quota = storageInfo.quotaGB;
                      const usedLabel = `${storageInfo.usedMB} MB`;
                      const ratio = storageInfo.usagePercent;
                      return (
                        <tr key={u.UserID} className="hover:bg-white transition-colors">
                          <td className="px-4 py-3 font-bold text-slate-900">{u.Name}</td>
                          <td className="px-4 py-3 text-slate-500">{u.Department} • {u.JobTitle}</td>
                          <td className="px-4 py-3 space-y-1">
                            <div className="flex justify-between text-[10px] text-slate-500 font-semibold">
                              <span>{usedLabel} / {quota} GB</span>
                              <span className={ratio > 80 ? 'text-rose-600 font-bold' : 'text-slate-600'}>{ratio.toFixed(1)}%</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full ${ratio > 80 ? 'bg-rose-500' : 'bg-indigo-600'}`}
                                style={{ width: `${ratio}%` }}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Quota Form */}
            <div className="bg-slate-50 border border-slate-200 p-5 rounded-2xl flex flex-col justify-between space-y-4">
              <form onSubmit={handleQuotaSubmit} className="space-y-4">
                <span className="text-xs font-bold text-indigo-700 uppercase tracking-wider block">용량 재할당 / 회수</span>
                
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-slate-600 block uppercase">대상 임직원</label>
                  <select
                    value={selectedUserId}
                    onChange={e => setSelectedUserId(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none"
                  >
                    {users.map(u => (
                      <option key={u.UserID} value={u.UserID}>{u.Name} ({u.Email})</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-slate-600 block uppercase">할당 용량 선택</label>
                  <select
                    value={newQuota}
                    onChange={e => setNewQuota(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none"
                  >
                    <option value="5">5 GB (일반 기본)</option>
                    <option value="10">10 GB (표준 권장)</option>
                    <option value="20">20 GB (중요 보관용)</option>
                    <option value="50">50 GB (대용량 첨부)</option>
                    <option value="100">100 GB (개발/서버)</option>
                  </select>
                </div>

                <button
                  type="submit"
                  className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm transition-colors cursor-pointer"
                >
                  할당 용량 즉시 적용
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* 4. Groups Tab */}
      {activeSubTab === 'groups' && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-6">
          <div>
            <h3 className="text-sm font-bold text-slate-900">대표 그룹 메일(Forwarding Group) 지정 관리</h3>
            <p className="text-xs text-slate-500 mt-1">대외 문의, 영업, 기술지원, 채용 등 대표 이메일 수신 시 해당 부서 담당자들에게 메일을 자동 분배 및 포워딩합니다.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {groupMails.map((group, idx) => (
              <div key={idx} className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4 flex flex-col justify-between hover:border-indigo-200 transition-all">
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="font-mono font-bold text-sm text-indigo-700 block">{group.GroupEmail}</span>
                      <span className="text-xs text-slate-600 font-semibold">{group.Description}</span>
                    </div>
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                      {group.Assignees.length}명 수신
                    </span>
                  </div>

                  <div className="border-t border-slate-200/80 pt-3">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2">지정 수신 담당자 ({group.Assignees.length})</span>
                    <div className="flex flex-wrap gap-1.5">
                      {group.Assignees.map((email, eIdx) => {
                        const matchedStaff = users.find(u => u.Email === email || (u.Aliases && u.Aliases.includes(email)));
                        return (
                          <span key={eIdx} className="bg-white border border-slate-200 px-2.5 py-1 rounded-xl text-xs text-slate-800 shadow-2xs flex items-center gap-1.5 font-medium">
                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                            {matchedStaff ? `${matchedStaff.Name} (${matchedStaff.Department} ${matchedStaff.JobTitle})` : email}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-200/80 flex justify-end">
                  <button
                    onClick={() => {
                      setEditingGroup(group);
                      setSelectedAssignees([...group.Assignees]);
                      setGroupSearchTerm('');
                    }}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold cursor-pointer shadow-sm transition-all flex items-center gap-1.5"
                  >
                    <UserCheck className="w-3.5 h-3.5" />
                    담당자 편집 (직원 목록에서 선택)
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 5. Logs Tab (Requirement 2) */}
      {activeSubTab === 'logs' && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-6">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
            <div>
              <h3 className="text-sm font-bold text-slate-900">AWS SES 실시간 수발신 감사(Audit) 로그</h3>
              <p className="text-xs text-slate-500 mt-1">Amazon SES API 및 인바운드 S3 이메일 파서를 통한 전사 실시간 수발신 감사 기록입니다.</p>
            </div>
            <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs px-3 py-1.5 rounded-xl font-bold self-start">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              SES 감사 시스템 활성 (HEALTHY)
            </span>
          </div>

          {/* Filter & Search Controls */}
          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <div className="flex bg-slate-100 p-1 rounded-xl gap-1">
              {[
                { id: 'ALL', label: '전체 로그' },
                { id: 'SEND', label: '발송(SEND)' },
                { id: 'RECEIVE', label: '수신(RECEIVE)' },
                { id: 'FAILED', label: '실패 건' }
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setLogFilterType(f.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    logFilterType === f.id ? 'bg-white text-indigo-700 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="감사 로그 검색 (송수신자, 제목, 에러사유)..."
                value={logSearchTerm}
                onChange={e => setLogSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
              />
            </div>
          </div>

          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full text-left text-xs text-slate-700">
              <thead className="bg-slate-50 text-[10px] font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3">일시</th>
                  <th className="px-4 py-3">유형</th>
                  <th className="px-4 py-3">송신자</th>
                  <th className="px-4 py-3">수신자</th>
                  <th className="px-4 py-3">메일 제목</th>
                  <th className="px-4 py-3">처리 상태</th>
                  <th className="px-4 py-3">상세 / ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sesLogs
                  .filter(log => {
                    if (logFilterType === 'SEND' && log.Type !== 'SEND') return false;
                    if (logFilterType === 'RECEIVE' && log.Type !== 'RECEIVE') return false;
                    if (logFilterType === 'FAILED' && log.Status !== 'FAILED') return false;
                    if (logSearchTerm) {
                      const term = logSearchTerm.toLowerCase();
                      const match = (log.Sender || '').toLowerCase().includes(term) ||
                                    (log.Recipient || '').toLowerCase().includes(term) ||
                                    (log.Subject || '').toLowerCase().includes(term) ||
                                    (log.ErrorMsg || '').toLowerCase().includes(term);
                      if (!match) return false;
                    }
                    return true;
                  })
                  .map(log => (
                    <tr key={log.LogID} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 text-xs text-slate-500 font-mono whitespace-nowrap">
                        {new Date(log.Timestamp).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
                          log.Type === 'SEND' 
                            ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' 
                            : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        }`}>
                          {log.Type === 'SEND' ? '발송' : '수신'}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-800">{log.Sender}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-800">{log.Recipient}</td>
                      <td className="px-4 py-3 text-slate-900 font-medium truncate max-w-[200px]" title={log.Subject}>{log.Subject}</td>
                      <td className="px-4 py-3">
                        {log.Status === 'SUCCESS' || log.Status === 'DELIVERED' ? (
                          <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-semibold">
                            <CheckCircle2 className="w-4 h-4" />
                            <span>{log.Status === 'DELIVERED' ? '정상 도달' : '발송 성공'}</span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-xs text-rose-600 font-semibold">
                            <XCircle className="w-4 h-4" />
                            <span>실패</span>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs font-mono max-w-[220px] truncate" title={log.ErrorMsg}>
                        {log.ErrorMsg || '정상 처리'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 6. Design Customization Tab */}
      {activeSubTab === 'design' && (
        <form onSubmit={handleSaveDesignSettings} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-6">
          <div>
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
              <Palette className="w-5 h-5 text-indigo-600" />
              <span>포털 브랜드 로고 및 폰트 커스터마이징</span>
            </h3>
            <p className="text-xs text-slate-500 mt-1">포털 좌측 사이드바 및 모바일 헤더의 로고 크기, 그리고 메뉴/본문 내용의 서체 및 크기를 직접 조정하고 전사 기기에 영구 동기화합니다.</p>
          </div>

          <div className="space-y-6 divide-y divide-slate-100">
            {/* Section 1: Logo Size */}
            <div className="space-y-4 pt-1">
              <h4 className="text-xs font-extrabold text-slate-700 uppercase tracking-wider">1. 로고 크기 설정 (Logo Size)</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 p-4 rounded-2xl border border-slate-200/60">
                {/* PC Logo size */}
                <div className="space-y-3 text-left">
                  <span className="text-[11px] font-bold text-slate-500 uppercase block">PC 브라우저용 로고 (데스크톱 사이드바)</span>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-extrabold text-slate-600 block">너비 (Width: {logoPcW}px)</label>
                      <input 
                        type="range" 
                        min="80" 
                        max="260" 
                        value={logoPcW}
                        onChange={e => setLogoPcW(Number(e.target.value))}
                        className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-extrabold text-slate-600 block">높이 (Height: {logoPcH}px)</label>
                      <input 
                        type="range" 
                        min="20" 
                        max="80" 
                        value={logoPcH}
                        onChange={e => setLogoPcH(Number(e.target.value))}
                        className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                      />
                    </div>
                  </div>
                </div>

                {/* Mobile Logo size */}
                <div className="space-y-3 text-left">
                  <span className="text-[11px] font-bold text-slate-500 uppercase block">모바일 웹 브라우저용 로고 (상단 헤더)</span>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-extrabold text-slate-600 block">너비 (Width: {logoMobileW}px)</label>
                      <input 
                        type="range" 
                        min="60" 
                        max="200" 
                        value={logoMobileW}
                        onChange={e => setLogoMobileW(Number(e.target.value))}
                        className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-extrabold text-slate-600 block">높이 (Height: {logoMobileH}px)</label>
                      <input 
                        type="range" 
                        min="15" 
                        max="60" 
                        value={logoMobileH}
                        onChange={e => setLogoMobileH(Number(e.target.value))}
                        className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                      />
                    </div>
                  </div>
                </div>

                {/* Login Logo size */}
                <div className="space-y-3 text-left">
                  <span className="text-[11px] font-bold text-slate-500 uppercase block">로그인 화면용 로고 (로그인 페이지)</span>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-extrabold text-slate-600 block">액자 너비 (Width: {loginLogoW}px)</label>
                      <input 
                        type="range" 
                        min="60" 
                        max="240" 
                        value={loginLogoW}
                        onChange={e => setLoginLogoW(Number(e.target.value))}
                        className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-extrabold text-slate-600 block">액자 높이 (Height: {loginLogoH}px)</label>
                      <input 
                        type="range" 
                        min="60" 
                        max="240" 
                        value={loginLogoH}
                        onChange={e => setLoginLogoH(Number(e.target.value))}
                        className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-extrabold text-slate-600 block">액자 여백 (Padding: {loginLogoPad}px)</label>
                      <input 
                        type="range" 
                        min="0" 
                        max="40" 
                        value={loginLogoPad}
                        onChange={e => setLoginLogoPad(Number(e.target.value))}
                        className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 1.5: Custom Logo Upload & Crop */}
            <div className="space-y-4 pt-5">
              <h4 className="text-xs font-extrabold text-slate-700 uppercase tracking-wider">1.5. 포털 로고 이미지 직접 변경 및 크롭 (Custom Logo Image)</h4>
              <p className="text-[11px] text-slate-500">포털 메인 화면, 사이드바, 헤더에 표시할 로고 이미지를 업로드하고 직사각형 가이드 영역에 맞춰 크롭하여 등록합니다.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-4 rounded-2xl border border-slate-200/60">
                {/* Left col: Upload & Controls */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <label className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all cursor-pointer inline-flex items-center gap-1.5 shadow-2xs">
                      <Upload className="w-3.5 h-3.5" />
                      <span>새 로고 파일 선택</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleLogoUpload}
                      />
                    </label>
                    
                    {customLogoUrl && (
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm('로고를 기본 Clouflake 로고로 초기화하시겠습니까?')) {
                            setCustomLogoUrl('');
                            setLogoRawImage(null);
                          }
                        }}
                        className="px-4 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl text-xs font-bold transition-all cursor-pointer"
                      >
                        기본 로고로 초기화
                      </button>
                    )}
                  </div>

                  {logoRawImage && (
                    <div className="space-y-3.5 pt-2">
                      <div className="space-y-1 text-left">
                        <label className="text-[10px] font-extrabold text-slate-500 flex justify-between">
                          <span>🔍 확대 / 축소 (Zoom)</span>
                          <span className="text-indigo-600 font-bold">{logoZoom.toFixed(2)}x</span>
                        </label>
                        <input
                          type="range"
                          min="0.5"
                          max="3"
                          step="0.05"
                          value={logoZoom}
                          onChange={(e) => setLogoZoom(parseFloat(e.target.value))}
                          className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                        />
                      </div>

                      <div className="space-y-1 text-left">
                        <label className="text-[10px] font-extrabold text-slate-500 flex justify-between">
                          <span>↔️ 좌우 이동 (X Offset)</span>
                          <span className="text-indigo-600 font-bold">{logoOffsetX}px</span>
                        </label>
                        <input
                          type="range"
                          min="-200"
                          max="200"
                          step="1"
                          value={logoOffsetX}
                          onChange={(e) => setLogoOffsetX(parseInt(e.target.value))}
                          className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                        />
                      </div>

                      <div className="space-y-1 text-left">
                        <label className="text-[10px] font-extrabold text-slate-500 flex justify-between">
                          <span>↕️ 상하 이동 (Y Offset)</span>
                          <span className="text-indigo-600 font-bold">{logoOffsetY}px</span>
                        </label>
                        <input
                          type="range"
                          min="-100"
                          max="100"
                          step="1"
                          value={logoOffsetY}
                          onChange={(e) => setLogoOffsetY(parseInt(e.target.value))}
                          className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Right col: Crop Guide Preview */}
                <div className="flex flex-col items-center justify-center p-4 bg-white rounded-xl border border-slate-200">
                  <span className="text-[10px] font-bold text-slate-400 mb-3 uppercase tracking-wider">Logo Crop Preview (200x60 가이드)</span>
                  
                  {/* Rectangular Crop Box (200px width, 60px height) */}
                  <div className="w-[200px] h-[60px] border-2 border-dashed border-indigo-500 bg-slate-100 flex items-center justify-center relative overflow-hidden rounded-md shadow-inner">
                    {logoRawImage ? (
                      <img
                        src={logoRawImage}
                        alt="Logo Crop Preview"
                        style={{
                          transform: `translate(${logoOffsetX}px, ${logoOffsetY}px) scale(${logoZoom})`,
                          transformOrigin: 'center',
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-slate-400 text-center">
                        <span className="text-[10px] font-bold">로고 없음</span>
                      </div>
                    )}
                  </div>
                  <span className="text-[9px] text-slate-400 mt-3 text-center">점선 직사각형 영역 내부가 로고로 크롭되어 포털 전역에 적용됩니다.</span>
                </div>
              </div>
            </div>

            {/* Section 2: Menu Font */}
            <div className="space-y-4 pt-5">
              <h4 className="text-xs font-extrabold text-slate-700 uppercase tracking-wider">2. 메뉴 및 헤더 폰트 설정 (Menu & UI Font)</h4>
              <p className="text-[11px] text-slate-400">사이드바 탭 메뉴, 결재 문서 헤더, 테이블 제목 등 시스템의 주요 메뉴 영역에 적용되는 서체 설정입니다.</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1 text-left">
                  <label className="text-[11px] font-bold text-slate-600 block">글꼴 종류 (Font Family)</label>
                  <select 
                    value={fontMenuFamily}
                    onChange={e => setFontMenuFamily(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-800 focus:outline-none"
                  >
                    <option value="Pretendard">Pretendard (한글 권장 고딕)</option>
                    <option value="Noto Sans KR">Noto Sans KR (구글 샌즈)</option>
                    <option value="Nanum Gothic">Nanum Gothic (나눔 고딕)</option>
                    <option value="Nanum Myeongjo">Nanum Myeongjo (나눔 명조)</option>
                    <option value="Inter">Inter (영문 전용 모던체)</option>
                    <option value="Roboto">Roboto (구글 시스템체)</option>
                    <option value="system-ui">OS System default (기본 폰트)</option>
                  </select>
                </div>

                <div className="space-y-1 text-left">
                  <label className="text-[11px] font-bold text-slate-600 block">글꼴 크기 (Font Size)</label>
                  <select 
                    value={fontMenuSize}
                    onChange={e => setFontMenuSize(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-800 focus:outline-none"
                  >
                    <option value="12">12px (작게)</option>
                    <option value="13">13px</option>
                    <option value="14">14px (표준)</option>
                    <option value="15">15px</option>
                    <option value="16">16px (크게)</option>
                    <option value="18">18px (매우 크게)</option>
                  </select>
                </div>

                <div className="space-y-1 text-left">
                  <label className="text-[11px] font-bold text-slate-600 block">글꼴 두께 (Font Weight)</label>
                  <select 
                    value={fontMenuWeight}
                    onChange={e => setFontMenuWeight(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-800 focus:outline-none"
                  >
                    <option value="light">Light (얇게)</option>
                    <option value="normal">Normal (보통)</option>
                    <option value="medium">Medium (약간 무겁게)</option>
                    <option value="bold">Bold (굵게)</option>
                    <option value="extrabold">Extra Bold (매우 굵게)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Section 3: Content Font */}
            <div className="space-y-4 pt-5">
              <h4 className="text-xs font-extrabold text-slate-700 uppercase tracking-wider">3. 본문 및 내용 폰트 설정 (Content & Body Font)</h4>
              <p className="text-[11px] text-slate-400">메일 본문, 게시판 설명글, 결재 내역 테이블 내용물 등 상세 텍스트 영역에 적용되는 서체 설정입니다.</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1 text-left">
                  <label className="text-[11px] font-bold text-slate-600 block">글꼴 종류 (Font Family)</label>
                  <select 
                    value={fontContentFamily}
                    onChange={e => setFontContentFamily(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-800 focus:outline-none"
                  >
                    <option value="Pretendard">Pretendard (한글 권장 고딕)</option>
                    <option value="Noto Sans KR">Noto Sans KR (구글 샌즈)</option>
                    <option value="Nanum Gothic">Nanum Gothic (나눔 고딕)</option>
                    <option value="Nanum Myeongjo">Nanum Myeongjo (나눔 명조)</option>
                    <option value="Inter">Inter (영문 전용 모던체)</option>
                    <option value="Roboto">Roboto (구글 시스템체)</option>
                    <option value="system-ui">OS System default (기본 폰트)</option>
                  </select>
                </div>

                <div className="space-y-1 text-left">
                  <label className="text-[11px] font-bold text-slate-600 block">글꼴 크기 (Font Size)</label>
                  <select 
                    value={fontContentSize}
                    onChange={e => setFontContentSize(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-800 focus:outline-none"
                  >
                    <option value="11">11px (아주 작게)</option>
                    <option value="12">12px (작게)</option>
                    <option value="13">13px (표준)</option>
                    <option value="14">14px</option>
                    <option value="15">15px</option>
                    <option value="16">16px (크게)</option>
                  </select>
                </div>

                <div className="space-y-1 text-left">
                  <label className="text-[11px] font-bold text-slate-600 block">글꼴 두께 (Font Weight)</label>
                  <select 
                    value={fontContentWeight}
                    onChange={e => setFontContentWeight(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-800 focus:outline-none"
                  >
                    <option value="light">Light (얇게)</option>
                    <option value="normal">Normal (보통)</option>
                    <option value="medium">Medium (약간 무겁게)</option>
                    <option value="bold">Bold (굵게)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-slate-100 flex justify-end gap-2">
            <button
              type="submit"
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-xs sm:text-sm rounded-xl cursor-pointer shadow-sm transition-all inline-flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              <span>디자인 적용 및 전사 동기화</span>
            </button>
          </div>
        </form>
      )}

      {/* 6. Kakao Business & Notification Integration Tab */}
      {activeSubTab === 'kakao' && (
        <div className="space-y-6 text-left animate-in fade-in duration-200">
          {/* Header Banner */}
          <div className="bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-400 rounded-3xl p-6 text-slate-950 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900/15 font-extrabold text-[11px] tracking-wide">
                <MessageSquare className="w-3.5 h-3.5" />
                카카오 비즈니스 파트너스 & 맞춤 템플릿 관리자
              </div>
              <h3 className="text-xl font-black tracking-tight text-slate-950">
                알림톡 템플릿 서식 커스터마이징 & 실시간 카카오톡 연동
              </h3>
              <p className="text-xs text-slate-900/80 font-medium">
                연차 신청, 경비 처리, 사내 공지, 메일 수신 요약, 결재 결과 알림톡 서식을 치환 변수 태그를 이용해 자유롭게 편집하고 관리합니다.
              </p>
            </div>

            <div className="flex items-center gap-2 bg-white/90 backdrop-blur-xs px-4 py-2.5 rounded-2xl border border-amber-300 shadow-xs">
              <span className="text-xs font-bold text-slate-800">카카오톡 연동 상태:</span>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-extrabold flex items-center gap-1.5 ${
                kEnabled ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-slate-200 text-slate-600'
              }`}>
                <span className={`w-2 h-2 rounded-full ${kEnabled ? 'bg-emerald-600 animate-pulse' : 'bg-slate-400'}`}></span>
                {kEnabled ? '실시간 연동 활성 (ON)' : '연동 일시 정지 (OFF)'}
              </span>
            </div>
          </div>

          {/* Toast Notification */}
          {kakaoSavedToast && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center justify-between animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="flex items-center gap-2.5 text-emerald-800 font-bold text-xs">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <span>카카오 비즈니스 연동 설정 및 맞춤 템플릿이 AWS DynamoDB에 안전하게 저장 및 동기화되었습니다.</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left 7 Cols: Template Manager & API Configuration */}
            <div className="lg:col-span-7 space-y-6">

              {/* CARD 1: Kakao Message Template Manager */}
              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-5">
                <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center font-black">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-sm font-black text-slate-900">알림톡 템플릿 맞춤 설정 및 치환 변수 관리</h4>
                      <p className="text-[11px] text-slate-500 font-medium">업무 상황별 알림 메시지 서식을 직접 수정하고 치환 변수를 배치할 수 있습니다.</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleResetTemplate(selectedTplKey)}
                    className="text-[11px] font-bold text-slate-500 hover:text-amber-700 bg-slate-100 hover:bg-amber-50 px-3 py-1.5 rounded-xl border border-slate-200 transition-colors flex items-center gap-1.5 cursor-pointer"
                    title="현재 템플릿을 기본 권장 서식으로 초기화합니다"
                  >
                    <RefreshCw className="w-3 h-3" />
                    <span>기본 서식 복원</span>
                  </button>
                </div>

                {/* 5 Template Navigation Tabs */}
                <div className="flex flex-wrap gap-1.5 p-1.5 bg-slate-100/80 rounded-2xl border border-slate-200">
                  {[
                    { id: 'LEAVE_REQUEST', label: '🌴 연차 결재 요청' },
                    { id: 'EXPENSE_REQUEST', label: '💳 경비 결재 요청' },
                    { id: 'NOTICE_BROADCAST', label: '📢 사내 공지사항' },
                    { id: 'MAIL_SUMMARY', label: '✉️ 메일 수신/요약' },
                    { id: 'APPROVAL_RESULT', label: '✅ 승인/반려 결과' }
                  ].map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => {
                        setSelectedTplKey(t.id);
                        setTestType(t.id);
                      }}
                      className={`px-3 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                        selectedTplKey === t.id
                          ? 'bg-amber-500 text-slate-950 shadow-xs font-black'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                      }`}
                    >
                      <span>{t.label}</span>
                    </button>
                  ))}
                </div>

                {/* Active Template Editor Form */}
                <div className="space-y-4 pt-1">
                  {/* Template Title */}
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700 flex items-center justify-between">
                      <span>메시지 타이틀 (Header Title)</span>
                      <span className="text-[10px] text-slate-400 font-normal">카카오톡 카드 상단에 노출되는 제목</span>
                    </label>
                    <input
                      type="text"
                      value={activeTpl.title || ''}
                      onChange={e => handleUpdateActiveTemplate('title', e.target.value)}
                      placeholder="예: [클라우플레이크 전자결재 알림]"
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  {/* Clickable Variable Tags Bar */}
                  <div className="space-y-1.5 bg-amber-50/60 p-3.5 rounded-2xl border border-amber-200/80">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-black text-amber-950 flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                        클릭하여 본문에 치환 변수 태그 자동 삽입
                      </span>
                      <span className="text-[10px] text-amber-800/80 font-semibold">발송 시 실제 데이터로 자동 치환됩니다</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {(activeTpl.availableVariables || []).map((v, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => handleInsertVariable(v.tag)}
                          className="px-2.5 py-1 bg-white hover:bg-amber-100 hover:border-amber-400 text-slate-800 hover:text-amber-950 text-[11px] font-bold rounded-lg border border-amber-300 shadow-2xs transition-all cursor-pointer flex items-center gap-1 group"
                          title={`예시: ${v.example} (${v.label})`}
                        >
                          <Plus className="w-3 h-3 text-amber-600 group-hover:scale-110 transition-transform" />
                          <span className="font-mono text-amber-900">{v.tag}</span>
                          <span className="text-[10px] text-slate-400 group-hover:text-amber-800">({v.label})</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Template Body Content Textarea */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-slate-700 block">메시지 본문 서식 (Template Body)</label>
                      <span className="text-[10px] text-slate-400 font-mono">
                        {(activeTpl.templateText || '').length}자 입력됨
                      </span>
                    </div>
                    <textarea
                      rows={8}
                      value={activeTpl.templateText || ''}
                      onChange={e => handleUpdateActiveTemplate('templateText', e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-mono text-slate-900 focus:outline-none focus:border-amber-500 leading-relaxed resize-y"
                      placeholder="알림톡 본문 템플릿을 입력하세요..."
                    />
                  </div>

                  {/* Button Configuration */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-700 flex items-center justify-between">
                        <span>하단 1-Click 버튼명</span>
                        <span className="text-[10px] text-amber-700 font-bold">최대 12자 이내 (이모지 제외)</span>
                      </label>
                      <input
                        type="text"
                        maxLength={12}
                        value={activeTpl.buttonText || ''}
                        onChange={e => handleUpdateActiveTemplate('buttonText', e.target.value)}
                        placeholder="예: 결재 승인하기"
                        className="w-full px-3.5 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-amber-500"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-700 block">버튼 클릭 시 이동 URL (Path)</label>
                      <input
                        type="text"
                        value={activeTpl.buttonUrl || ''}
                        onChange={e => handleUpdateActiveTemplate('buttonUrl', e.target.value)}
                        placeholder="예: /#/approvals"
                        className="w-full px-3.5 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-mono text-slate-900 focus:outline-none focus:border-amber-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-100 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSaveKakaoConfig}
                    className="px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black text-xs rounded-xl cursor-pointer shadow-xs transition-all inline-flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    <span>템플릿 변경사항 저장 및 DynamoDB 동기화</span>
                  </button>
                </div>
              </div>

              {/* CARD 2: Kakao API Credentials & Channel Configuration */}
              <form onSubmit={handleSaveKakaoConfig} className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-6">
                <div className="flex justify-between items-center pb-4 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
                      <Shield className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-sm font-bold text-slate-900 block">카카오 비즈니스 API 인증 및 중계사 설정</span>
                      <span className="text-[11px] text-slate-500">Solapi / 알리고 / NHN Cloud API 연동 키를 관리합니다.</span>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={kEnabled}
                      onChange={e => setKEnabled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                  </label>
                </div>

                {/* Provider Selection */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-700 block">비즈메시지 게이트웨이 / 중계사 선택</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    {[
                      { id: 'solapi', name: 'Solapi (CoolSMS)', badge: '권장 (친구톡 완벽지원)' },
                      { id: 'aligo', name: '알리고 (Aligo)', badge: '대량 발송' },
                      { id: 'nhn', name: 'NHN Cloud', badge: '엔터프라이즈' },
                      { id: 'direct', name: '카카오 비즈니스 직접연동', badge: 'REST API' }
                    ].map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setKProvider(p.id)}
                        className={`p-3 rounded-2xl border text-left transition-all cursor-pointer flex flex-col justify-between gap-1.5 ${
                          kProvider === p.id 
                            ? 'border-amber-500 bg-amber-50/70 ring-2 ring-amber-400/20 text-slate-900 font-bold' 
                            : 'border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600'
                        }`}
                      >
                        <span className="text-xs">{p.name}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-extrabold w-fit ${
                          kProvider === p.id ? 'bg-amber-400 text-slate-950' : 'bg-slate-200 text-slate-500'
                        }`}>{p.badge}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Channel & Profile Keys */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700 block">카카오톡 채널 ID</label>
                    <input
                      type="text"
                      placeholder="예: @clouflake"
                      value={kChannelId}
                      onChange={e => setKChannelId(e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-amber-500 font-medium"
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700 block">발신프로필 키 (Sender Profile Key / PFID)</label>
                    <input
                      type="text"
                      placeholder="카카오 비즈니스 발신프로필 키"
                      value={kSenderKey}
                      onChange={e => setKSenderKey(e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-amber-500 font-mono"
                    />
                  </div>
                </div>

                {/* API Key & Secret */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700 block">API Key (Access Key)</label>
                    <input
                      type="text"
                      placeholder="중계사 발급 API Key"
                      value={kApiKey}
                      onChange={e => setKApiKey(e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-amber-500 font-mono"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <label className="text-xs font-bold text-slate-700 block">API Secret (Secret Key)</label>
                      <button
                        type="button"
                        onClick={() => setShowSecret(!showSecret)}
                        className="text-[10px] text-slate-500 hover:text-slate-800 flex items-center gap-1 cursor-pointer"
                      >
                        {showSecret ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        {showSecret ? '숨기기' : '보기'}
                      </button>
                    </div>
                    <input
                      type={showSecret ? 'text' : 'password'}
                      placeholder="중계사 발급 API Secret"
                      value={kApiSecret}
                      onChange={e => setKApiSecret(e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-amber-500 font-mono"
                    />
                  </div>
                </div>

                {/* Representative Phone */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 block">사내 발신 대표 번호 (SMS 대체 발송용)</label>
                  <input
                    type="text"
                    placeholder="예: 02-123-4567"
                    value={kSenderPhone}
                    onChange={e => setKSenderPhone(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-amber-500"
                  />
                </div>

                {/* Automated Triggers */}
                <div className="space-y-3 pt-2 border-t border-slate-100">
                  <span className="text-xs font-bold text-slate-800 block">카카오톡 자동 발송 정책 및 트리거 설정</span>
                  
                  <div className="space-y-2">
                    <label className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 border border-slate-200 cursor-pointer hover:bg-slate-100/80 transition-colors">
                      <div className="space-y-0.5">
                        <span className="text-xs font-bold text-slate-900 block">📢 공지사항 등록 시 전 직원 카톡 자동 발송</span>
                        <p className="text-[11px] text-slate-500">대시보드에서 공지 작성 시 재직 중인 전 직원 전화번호로 실시간 카톡 전송</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={kAutoNotice}
                        onChange={e => setKAutoNotice(e.target.checked)}
                        className="w-4 h-4 rounded text-amber-500 focus:ring-amber-400 border-slate-300 cursor-pointer"
                      />
                    </label>

                    <label className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 border border-slate-200 cursor-pointer hover:bg-slate-100/80 transition-colors">
                      <div className="space-y-0.5">
                        <span className="text-xs font-bold text-slate-900 block">🤖 수신 메일 3줄 AI 요약 알림 카톡 발송</span>
                        <p className="text-[11px] text-slate-500">신규 메일 수신 시 중요 안건 및 Action Item을 AI가 요약하여 수신자에게 카톡 전송</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={kAutoMailSummary}
                        onChange={e => setKAutoMailSummary(e.target.checked)}
                        className="w-4 h-4 rounded text-amber-500 focus:ring-amber-400 border-slate-300 cursor-pointer"
                      />
                    </label>

                    <label className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 border border-slate-200 cursor-pointer hover:bg-slate-100/80 transition-colors">
                      <div className="space-y-0.5">
                        <span className="text-xs font-bold text-slate-900 block">📱 카톡 미수신 시 LMS/SMS 자동 대체 발송 (Failover)</span>
                        <p className="text-[11px] text-slate-500">카카오톡 미설치자 또는 채널 차단자에게 일반 문자로 100% 도달 보장</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={kFailoverSms}
                        onChange={e => setKFailoverSms(e.target.checked)}
                        className="w-4 h-4 rounded text-amber-500 focus:ring-amber-400 border-slate-300 cursor-pointer"
                      />
                    </label>
                  </div>
                </div>

                <div className="pt-2 flex justify-end">
                  <button
                    type="submit"
                    className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black text-xs sm:text-sm rounded-xl cursor-pointer shadow-sm transition-all inline-flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    <span>카카오 비즈니스 연동 전체 저장</span>
                  </button>
                </div>
              </form>
            </div>

            {/* Right 5 Cols: Real-time Smartphone Live Preview & Test Sender */}
            <div className="lg:col-span-5 space-y-6">
              {/* Smartphone KakaoTalk Live Bubble Mockup */}
              <div className="bg-[#abc1d1] rounded-3xl p-5 border-4 border-slate-800 shadow-xl space-y-3 sticky top-4">
                <div className="flex items-center justify-between text-slate-800 text-[11px] font-bold px-1">
                  <span className="flex items-center gap-1.5">
                    <Smartphone className="w-3.5 h-3.5" />
                    실시간 알림톡 프리뷰
                  </span>
                  <span className="text-[10px] bg-slate-900/20 px-2 py-0.5 rounded-full font-mono font-bold">
                    {activeTpl.name || selectedTplKey}
                  </span>
                </div>

                {/* Message Bubble Card */}
                <div className="bg-white rounded-2xl p-4 shadow-md space-y-3 border border-slate-200/80 text-left">
                  {/* Channel Header */}
                  <div className="flex items-center gap-2 pb-2.5 border-b border-slate-100">
                    <div className="w-7 h-7 rounded-full bg-amber-400 flex items-center justify-center font-black text-slate-900 text-xs shadow-xs">
                      C
                    </div>
                    <div>
                      <span className="font-bold text-xs text-slate-900 block">주식회사 클로우플레이크</span>
                      <span className="text-[10px] text-slate-400 font-medium">{kChannelId} 비즈니스 채널</span>
                    </div>
                  </div>

                  {/* Body Header */}
                  <div className="space-y-1">
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-extrabold bg-amber-100 text-amber-900">
                      {activeTpl.name || '전자결재 알림'}
                    </span>
                    <h4 className="text-xs font-black text-slate-900 leading-snug">
                      {activeTpl.title || '[클라우플레이크 전자결재 알림]'}
                    </h4>
                  </div>

                  {/* Body Content with Substituted Preview */}
                  <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 text-[11px] text-slate-800 leading-relaxed whitespace-pre-line font-medium">
                    {getRenderedPreview(selectedTplKey)}
                  </div>

                  {/* 1-Click Button */}
                  <div className="pt-1">
                    <a
                      href="#"
                      onClick={e => e.preventDefault()}
                      className="block w-full py-2 bg-[#fee500] hover:bg-[#ebd300] text-[#191919] font-black text-xs text-center rounded-xl transition-colors shadow-2xs"
                    >
                      {activeTpl.buttonText || '확인하기'}
                    </a>
                  </div>

                  {/* Kakao Required Footer */}
                  <div className="pt-2 border-t border-slate-100 text-[9px] text-slate-400 leading-tight">
                    * 본 메시지는 클로우플레이크 AX 통합 플랫폼에서 업무 편의를 위해 발송되었습니다.
                  </div>
                </div>

                {/* Quick Live Test Sender inside phone panel */}
                <div className="bg-white/95 backdrop-blur-xs rounded-2xl p-4 border border-slate-300 shadow-sm space-y-3 text-left">
                  <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                    <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <Send className="w-3.5 h-3.5 text-amber-600" />
                      현재 서식으로 테스트 발송
                    </span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded font-extrabold bg-amber-100 text-amber-800">Live</span>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-600 block">수신 휴대폰 번호</label>
                    <input
                      type="text"
                      placeholder="010-0000-0000"
                      value={testPhone}
                      onChange={e => setTestPhone(e.target.value)}
                      className="w-full px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-xl text-xs text-slate-900 font-bold focus:outline-none focus:border-amber-500 font-mono"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleSendKakaoTest}
                    disabled={isSendingTest}
                    className="w-full py-2 bg-slate-950 hover:bg-slate-800 text-amber-400 font-black text-xs rounded-xl cursor-pointer shadow-sm transition-all flex items-center justify-center gap-1.5"
                  >
                    {isSendingTest ? (
                      <>
                        <RefreshCw className="w-3.5 h-3.5 animate-spin text-amber-400" />
                        <span>카카오톡 발송 중...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5" />
                        <span>🚀 선택된 서식 카톡 테스트 발송</span>
                      </>
                    )}
                  </button>

                  {testResult && (
                    <div className={`p-2.5 rounded-xl border text-[11px] font-bold ${
                      testResult.success 
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-800' 
                        : 'bg-rose-50 border-rose-200 text-rose-800'
                    }`}>
                      {testResult.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Kakao Dispatch Audit Logs Table */}
          <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center font-bold">
                  <Server className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-900">카카오톡 발송 이력 및 전송 감사 로그 (Audit Logs)</h4>
                  <p className="text-xs text-slate-500">최근 전 직원 공지 및 메일 AI 요약 카카오톡 발송 성공/실패 내역을 실시간으로 추적합니다.</p>
                </div>
              </div>
              <span className="text-xs font-bold text-slate-600 bg-slate-100 px-3 py-1.5 rounded-xl">
                총 <strong className="text-indigo-600 font-bold">{kakaoDispatchLogs?.length || 0}</strong>건 기록됨
              </span>
            </div>

            <div className="overflow-x-auto border border-slate-200 rounded-2xl">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold">
                  <tr>
                    <th className="py-3 px-4">발송 일시</th>
                    <th className="py-3 px-4">수신자</th>
                    <th className="py-3 px-4">수신 번호</th>
                    <th className="py-3 px-4">유형</th>
                    <th className="py-3 px-4">메시지 제목 및 요약 내용</th>
                    <th className="py-3 px-4 text-center">전송 상태</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {(!kakaoDispatchLogs || kakaoDispatchLogs.length === 0) ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-slate-400 font-medium">
                        발송된 카카오톡 이력이 없습니다.
                      </td>
                    </tr>
                  ) : (
                    kakaoDispatchLogs.map(log => (
                      <tr key={log.id} className="hover:bg-slate-50/70 transition-colors">
                        <td className="py-3 px-4 text-slate-400 font-mono text-[11px] whitespace-nowrap">
                          {new Date(log.sentAt).toLocaleString('ko-KR')}
                        </td>
                        <td className="py-3 px-4 font-bold text-slate-900">
                          {log.recipientName}
                        </td>
                        <td className="py-3 px-4 font-mono text-slate-600">
                          {log.recipientPhone}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
                            log.type === 'NOTICE' 
                              ? 'bg-amber-100 text-amber-800 border border-amber-200' 
                              : 'bg-indigo-100 text-indigo-800 border border-indigo-200'
                          }`}>
                            {log.type === 'NOTICE' ? '📢 사내 공지' : '🤖 메일 AI 요약'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-slate-700 max-w-md">
                          <div className="font-bold text-slate-900 truncate">
                            {typeof log.title === 'string' ? log.title : (log.title?.title || String(log.title || ''))}
                          </div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {typeof log.summaryText === 'string' ? log.summaryText : (log.summaryText?.messageText || JSON.stringify(log.summaryText || ''))}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center whitespace-nowrap">
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold inline-flex items-center gap-1 ${
                            log.status === 'SUCCESS' 
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                              : 'bg-rose-50 text-rose-700 border border-rose-200'
                          }`}>
                            <CheckCircle className="w-3 h-3 text-emerald-600" />
                            전송 완료 (200 OK)
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Representative Group Mail Assignee Editor Modal (Requirement 1) */}
      {editingGroup && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-xl w-full shadow-2xl space-y-5 text-left animate-in fade-in zoom-in-95 duration-150 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">대표 그룹 메일 수신 담당자 편집</h3>
                  <span className="text-xs text-indigo-600 font-mono font-bold">{editingGroup.GroupEmail} ({editingGroup.Description})</span>
                </div>
              </div>
              <button
                onClick={() => setEditingGroup(null)}
                className="p-1 rounded-xl text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Currently Selected Chips */}
            <div className="space-y-1.5 bg-slate-50 p-3.5 rounded-2xl border border-slate-200">
              <div className="flex justify-between items-center text-xs">
                <span className="font-bold text-slate-700">선택된 수신 담당자 ({selectedAssignees.length}명)</span>
                {selectedAssignees.length > 0 && (
                  <button 
                    onClick={() => setSelectedAssignees([])}
                    className="text-[11px] text-rose-600 font-bold hover:underline cursor-pointer"
                  >
                    전체 해제
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 min-h-[32px]">
                {selectedAssignees.length === 0 ? (
                  <span className="text-xs text-slate-400 italic">아래 직원 목록에서 담당자를 선택해 주세요.</span>
                ) : (
                  selectedAssignees.map((email, idx) => {
                    const staff = users.find(u => u.Email === email || (u.Aliases && u.Aliases.includes(email)));
                    return (
                      <span key={idx} className="bg-white border border-indigo-200 text-indigo-900 px-2.5 py-1 rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-2xs">
                        <span>{staff ? `${staff.Name} (${staff.Department})` : email}</span>
                        <button
                          onClick={() => setSelectedAssignees(prev => prev.filter(e => e !== email))}
                          className="text-slate-400 hover:text-rose-600 p-0.5 rounded cursor-pointer"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })
                )}
              </div>
            </div>

            {/* Staff Search Input */}
            <div className="relative">
              <Search className="absolute left-3.5 top-3 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="직원 이름, 부서, 직급, 이메일 검색..."
                value={groupSearchTerm}
                onChange={e => setGroupSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
              />
            </div>

            {/* Employee List */}
            <div className="overflow-y-auto max-h-60 divide-y divide-slate-100 border border-slate-200 rounded-2xl">
              {users
                .filter(u => u.Status === 'Active')
                .filter(u => {
                  if (!groupSearchTerm) return true;
                  const term = groupSearchTerm.toLowerCase();
                  return (u.Name || '').toLowerCase().includes(term) ||
                         (u.Department || '').toLowerCase().includes(term) ||
                         (u.JobTitle || '').toLowerCase().includes(term) ||
                         (u.Email || '').toLowerCase().includes(term) ||
                         (u.Aliases || '').toLowerCase().includes(term);
                })
                .map(user => {
                  const isAssigned = selectedAssignees.includes(user.Email);
                  return (
                    <div
                      key={user.UserID}
                      onClick={() => {
                        if (isAssigned) {
                          setSelectedAssignees(prev => prev.filter(e => e !== user.Email));
                        } else {
                          setSelectedAssignees(prev => [...prev, user.Email]);
                        }
                      }}
                      className={`p-3 flex items-center justify-between cursor-pointer transition-colors ${
                        isAssigned ? 'bg-indigo-50/70 hover:bg-indigo-100/70' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
                          isAssigned ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-700'
                        }`}>
                          {user.Name.slice(0, 1)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-xs text-slate-900">{user.Name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold">
                              {user.Department} · {user.JobTitle}
                            </span>
                          </div>
                          <span className="text-[11px] text-slate-400 font-mono">{user.Email}</span>
                        </div>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={isAssigned}
                          onChange={() => {}} // Handled by div click
                          className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 pointer-events-none"
                        />
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* Modal Actions */}
            <div className="pt-2 flex justify-end gap-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setEditingGroup(null)}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl cursor-pointer"
              >
                취소
              </button>
              <button
                type="button"
                onClick={() => {
                  updateGroupMailAssignees(editingGroup.GroupEmail, selectedAssignees);
                  setEditingGroup(null);
                  alert(`${editingGroup.GroupEmail} 대표 그룹 메일 담당자가 ${selectedAssignees.length}명으로 정상 업데이트되었습니다.`);
                }}
                className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl cursor-pointer shadow-sm flex items-center gap-1.5"
              >
                <CheckCircle className="w-4 h-4" />
                저장 및 포워딩 규칙 적용
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailSystemSettings;
